import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Clock,
  Users,
  GraduationCap,
  CheckCircle,
  Target,
  Sparkle,
  Compass,
  BookOpen,
  Lightbulb,
  ChartLineUp,
  ClipboardText,
} from '@phosphor-icons/react';

const OBJECTIVES = [
  'فهم الفلسفة المونتيسورية بمصادرها الأصيلة وتطورها المعاصر',
  'تطبيق المبادئ المونتيسورية داخل البيئة الصفية اليومية',
  'تصميم بيئة تعليمية مهيّأة تدعم استقلالية الطفل',
  'استخدام الأدوات المونتيسورية في خمسة مجالات تعليمية',
  'تطوير مهارات الملاحظة المنظمة وتوجيه التعلم الفردي',
  'بناء علاقة احترافية بين الكبار والأطفال داخل البيئة المونتيسورية',
];

const MODULES = [
  {
    icon: Compass,
    title: 'الوحدة الأولى — الإطار الفلسفي',
    items: [
      'تطور فلسفة مونتيسوري وأسسها العلمية',
      'النظرة إلى الطفل ومراحل النمو الحساسة',
      'دور الكبار في البيئة المونتيسورية',
    ],
  },
  {
    icon: BookOpen,
    title: 'الوحدة الثانية — البيئة المهيّأة',
    items: [
      'مبادئ تصميم الفصل المونتيسوري',
      'تنظيم المساحات والمواد التعليمية',
      'بناء روتين يومي متّسق وحرّ في الوقت ذاته',
    ],
  },
  {
    icon: ClipboardText,
    title: 'الوحدة الثالثة — المجالات التعليمية الخمسة',
    items: [
      'الحياة العملية',
      'التربية الحسية',
      'اللغة',
      'الرياضيات',
      'الثقافة والعلوم',
    ],
  },
  {
    icon: Lightbulb,
    title: 'الوحدة الرابعة — العرض والتوجيه',
    items: [
      'فن العرض المونتيسوري (Presentation)',
      'الملاحظة المنظمة كأداة قيادة للتعلم',
      'سجلات التتبع وقراءة بيانات الطفل',
    ],
  },
  {
    icon: ChartLineUp,
    title: 'الوحدة الخامسة — التطبيق والتقييم',
    items: [
      'مشروع تطبيقي داخل البيئة الصفية الفعلية',
      'مراجعة مهنية + تغذية راجعة',
      'خطة تطوير شخصية بعد البرنامج',
    ],
  },
];

const FOR_WHO = [
  'معلمات ومربيات الطفولة المبكرة الراغبات في تطبيق المنهج المونتيسوري',
  'قيادات منشآت رياض الأطفال والحضانات',
  'مؤسسي مشاريع الطفولة المبكرة الجديدة',
  'مختصات تطوير المناهج في القطاع التعليمي',
];

const MontessoriProgramPage = () => (
  <main
    data-testid="program-montessori-page"
    className="pt-24 pb-20 min-h-screen bg-[#FAFAF7]"
    dir="rtl"
  >
    {/* Hero */}
    <section className="bg-[#1F3D2B] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/programs"
            className="inline-flex items-center gap-1 text-xs sm:text-sm text-[#D8E0C9] hover:text-white mb-5 transition-colors"
          >
            ← العودة لكتالوج البرامج
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="inline-block px-4 py-1.5 rounded-full border border-white/30 text-[#D8E0C9] text-xs sm:text-sm">
              فلسفات تربوية تطبيقية
            </span>
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#4A641F] text-white text-xs sm:text-sm font-bold">
              متاح للتسجيل
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight font-['IBM_Plex_Sans_Arabic'] mb-3">
            البرنامج التطبيقي القائم على فلسفة مونتيسوري
          </h1>
          <p className="text-xs sm:text-sm text-[#A3B5A0] mb-5" dir="ltr">
            Applied Program Based on the Montessori Philosophy
          </p>
          <p className="text-base sm:text-lg text-[#D8E0C9] leading-9 max-w-3xl">
            برنامج تطبيقي متكامل يأخذ المشاركات في رحلة منظمة من فهم الفلسفة
            المونتيسورية الأصيلة إلى تطبيقها كممارسة يومية داخل البيئة الصفية،
            بأسلوب مهني يجمع بين العمق المعرفي والتدريب العملي والإشراف المرحلي.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/quotation?service=program-montessori-applied"
              data-testid="montessori-enroll-btn"
              className="bg-white text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl px-7 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              اطلب عرض سعر للجهة
              <ArrowLeft size={16} className="rtl-flip" />
            </Link>
            <Link
              to="/contact"
              className="border border-white/40 text-white hover:bg-white/10 rounded-xl px-7 py-3.5 text-sm font-medium inline-flex items-center justify-center transition-colors"
            >
              تواصل لمعرفة التفاصيل
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Quick facts */}
    <section className="py-12 bg-[#F5F2EC]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Clock, label: 'المدة', value: '٨–١٢ أسبوعاً' },
          { icon: GraduationCap, label: 'النمط', value: 'تطبيقي + إشراف مرحلي' },
          { icon: Users, label: 'الفئة', value: 'كادر الطفولة المبكرة' },
        ].map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              className="bg-white border border-[#E6E0D6] rounded-2xl p-5 flex items-center gap-3"
            >
              <div className="w-11 h-11 rounded-xl bg-[#F5F2EC] flex items-center justify-center shrink-0">
                <Icon size={22} weight="duotone" className="text-[#4A641F]" />
              </div>
              <div>
                <p className="text-xs text-[#92A082]">{f.label}</p>
                <p className="text-sm font-bold text-[#1F3D2B]">{f.value}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>

    {/* Objectives */}
    <section className="py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_1.3fr] gap-10">
        <div>
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            الأهداف
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic'] leading-tight">
            ماذا ستحققه المشاركات في هذا البرنامج
          </h2>
          <p className="text-[#6B7565] leading-9 text-sm sm:text-base">
            صُمّمت الأهداف بحيث تخرج كل مشاركة قادرة على تطبيق ما تعلمته بشكل
            مباشر في البيئة الصفية، وليس مجرد فهم نظري.
          </p>
        </div>
        <ul className="space-y-3">
          {OBJECTIVES.map((o, i) => (
            <motion.li
              key={o}
              initial={{ opacity: 0, x: 14 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04, duration: 0.4 }}
              className="bg-white border border-[#E6E0D6] rounded-2xl p-5 flex items-start gap-3 text-sm text-[#3D512D] leading-8"
            >
              <Target size={18} weight="duotone" className="text-[#4A641F] mt-0.5 shrink-0" />
              <span>{o}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>

    {/* Modules */}
    <section className="py-16 sm:py-20 bg-[#F5F2EC]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            بنية البرنامج
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            خمس وحدات تطبيقية متسلسلة
          </h2>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8 text-sm sm:text-base">
            رحلة منظمة تأخذ المشاركة من الفهم النظري إلى التطبيق الميداني الكامل.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="bg-white border border-[#E6E0D6] rounded-2xl p-6 relative"
              >
                <span className="absolute top-4 left-4 text-xs text-[#92A082] font-bold">
                  0{i + 1}
                </span>
                <div className="w-11 h-11 rounded-xl bg-[#F5F2EC] flex items-center justify-center mb-3">
                  <Icon size={22} weight="duotone" className="text-[#4A641F]" />
                </div>
                <h3 className="text-base font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic'] leading-tight">
                  {m.title}
                </h3>
                <ul className="space-y-1.5">
                  {m.items.map((it) => (
                    <li
                      key={it}
                      className="flex items-start gap-1.5 text-sm text-[#3D512D] leading-7"
                    >
                      <span className="w-1 h-1 mt-2.5 rounded-full bg-[#4A641F] shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* For who */}
    <section className="py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            الفئات المستهدفة
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            لمن هذا البرنامج
          </h2>
        </motion.div>
        <ul className="grid sm:grid-cols-2 gap-3">
          {FOR_WHO.map((f, i) => (
            <motion.li
              key={f}
              initial={{ opacity: 0, x: 14 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04, duration: 0.4 }}
              className="bg-white border border-[#E6E0D6] rounded-2xl p-5 flex items-start gap-3 text-sm text-[#3D512D] leading-8"
            >
              <CheckCircle
                size={18}
                weight="fill"
                className="text-[#4A641F] mt-0.5 shrink-0"
              />
              <span>{f}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-[#1F3D2B]">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <Sparkle size={32} weight="duotone" className="text-[#D8E0C9] mx-auto mb-3" />
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 font-['IBM_Plex_Sans_Arabic']">
          جاهزون لتفعيل البرنامج داخل منشأتكم؟
        </h2>
        <p className="text-[#D8E0C9] leading-9 text-sm sm:text-base mb-7">
          نطبّق البرنامج بصيغة مؤسسية مخصصة (In-House) مع منشآت الطفولة المبكرة،
          أو ضمن مجموعات مفتوحة. تواصلوا معنا لمناقشة الصيغة الأنسب لجهتكم.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/quotation?service=program-montessori-applied"
            className="bg-white text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl px-7 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
          >
            اطلب عرض سعر مخصص
            <ArrowLeft size={16} className="rtl-flip" />
          </Link>
          <Link
            to="/contact"
            className="border border-white/40 text-white hover:bg-white/10 rounded-xl px-7 py-3.5 text-sm font-medium inline-flex items-center justify-center transition-colors"
          >
            تواصل معنا
          </Link>
        </div>
      </div>
    </section>
  </main>
);

export default MontessoriProgramPage;
